import { useState } from 'react';

import { Link, useNavigate, useRouter } from '@tanstack/react-router';

import { Form, Submit, TextField } from '@src/corelib/forms/generic';
import { createSchema, Yup } from '@src/corelib/validation';
import { useCreateSellerMutation } from '@src/query';

import { useOnboarding } from './OnboardingContext';
import OnboardingShell from './OnboardingShell';


const schema = createSchema({
  location_name: Yup.string().required('Name this location'),
  location_address: Yup.string(),
  location_city: Yup.string(),
  location_region: Yup.string(),
  location_postal_code: Yup.string(),
});


function AddLocation() {
  const navigate = useNavigate();
  const router = useRouter();
  const { draft, update } = useOnboarding();
  const createSeller = useCreateSellerMutation();
  const [serverError, setServerError] = useState(null);

  const onSubmit = (values) => {
    update(values);
    setServerError(null);
    return createSeller.mutateAsync({
      payload: {
        name: draft.name,
        contact_email: draft.contact_email,
        contact_phone: draft.contact_phone,
        segment: draft.segment,
        // Collected in the create-org step (defaults to the browser zone).
        timezone: draft.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        ...values,
      },
    }).then(() => {
      return router.invalidate().finally(() => navigate({ to: '/app/dashboard' }));
    }).catch((error) => {
      if (!error?.response || error.response.status >= 500) {
        setServerError('Something went wrong. Please try again.');
      }
      throw error;
    });
  };

  return (
    <OnboardingShell
      step={2}
      width="max-w-[460px]"
      title="Where's your first yard?"
      sub="Add the shop or yard where this inventory lives — you can add more anytime."
      backTo="/onboarding/org"
    >
      {serverError
        ? (
          <div className="w-full rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">{serverError}</div>
        )
        : null}

      <Form
        schema={schema}
        className="w-full text-left"
        defaultValues={{
          location_name: draft.location_name,
          location_address: draft.location_address,
          location_city: draft.location_city,
          location_region: draft.location_region,
          location_postal_code: draft.location_postal_code,
        }}
        onSubmit={onSubmit}
      >
        <TextField name="location_name" label="Location name" placeholder="Main Yard" />
        <TextField label="Street address" name="location_address" placeholder="123 Main St" />

        <div className="flex gap-3">
          <TextField label="City" name="location_city" className="flex-[2]" placeholder="Springfield" />
          <TextField label="State" className="flex-1" name="location_region" placeholder="IL" />
          <TextField label="ZIP" className="flex-1" name="location_postal_code" placeholder="62701" />
        </div>

        <Submit fullWidth className="h-[46px] max-lg:h-[50px]">Add location &amp; finish</Submit>
      </Form>

      <Link className="text-table font-medium text-primary" to="/onboarding/org">
        ← Back
      </Link>
    </OnboardingShell>
  );
}

AddLocation.displayName = 'AddLocation';
export default AddLocation;
